<template>
    <section class="slice">
        <div :class="{'slice-content': !isResourcesCenter}">
            <h2 v-if="slice.primary.title && !slice.primary.logo?.url" class="slice-title text-left">{{ slice.primary.title }}</h2>
            <div class="slice-body">
                <MktVersus
                    :title="slice.primary.title"
                    :logo-url="slice.primary.logo?.url"
                    :logo-alt="slice.primary.logo?.alt"
                    :features-title="slice.primary.featuresTitle ?? ''"
                    :features="features"
                    :options="options"
                    :options-checks="optionsChecks"
                    :cta-text="slice.primary.ctaText"
                    :cta-url="slice.primary.ctaLink"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import type {BooleanField, KeyTextField} from '@prismicio/types';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {VersusSlice} from '@prismicio-types';
    import {MktVersus} from '#components';
    import type {OptionHeader, OptionValue} from '../../../components/versus/Versus.vue';
    import useModuleConfig from '../../../composables/useModuleConfig';

    type VersusDefaultInZone = Extract<VersusSlice, {variation: 'default'}>;

    const props = defineProps(getSliceComponentProps<VersusDefaultInZone>(['slice', 'index', 'slices', 'context']));

    const isResourcesCenter = useModuleConfig().theme === 'resources-center';

    const features = props.slice.primary.features.map((feature) => feature.featureName) as string[];
    const options = [
        props.slice.primary.mainOptionLogo?.url
            ? {type: 'logo', url: props.slice.primary.mainOptionLogo.url, alt: props.slice.primary.mainOptionLogo.alt}
            : {type: 'title', text: props.slice.primary.mainOptionTitle},
        props.slice.primary.option1Logo?.url
            ? {type: 'logo', url: props.slice.primary.option1Logo.url, alt: props.slice.primary.option1Logo.alt}
            : {type: 'title', text: props.slice.primary.option1Title},
        props.slice.primary.option2Logo?.url
            ? {type: 'logo', url: props.slice.primary.option2Logo.url, alt: props.slice.primary.option2Logo.alt}
            : props.slice.primary.option2Title
              ? {type: 'title', text: props.slice.primary.option2Title}
              : null,
        props.slice.primary.option3Logo?.url
            ? {type: 'logo', url: props.slice.primary.option3Logo.url, alt: props.slice.primary.option3Logo.alt}
            : props.slice.primary.option3Title
              ? {type: 'title', text: props.slice.primary.option3Title}
              : null,
    ].filter((option) => option !== null) as OptionHeader[];

    const getOptionChecks = (optionIndex: number) => {
        switch (optionIndex) {
            case 0:
                return props.slice.primary.mainOptionChecks;
            case 1:
                return props.slice.primary.option1Checks;
            case 2:
                return props.slice.primary.option2Checks;
            case 3:
                return props.slice.primary.option3Checks;
            default:
                return [];
        }
    };
    const optionsChecks: OptionValue[][] = [];

    for (let optionIndex = 0; optionIndex < options.length; optionIndex++) {
        for (let featureIndex = 0; featureIndex < features.length; featureIndex++) {
            if (featureIndex === 0) {
                optionsChecks.push([]);
            }

            const optionNChecks = getOptionChecks(optionIndex);
            if (optionNChecks.length < featureIndex + 1) {
                optionsChecks[optionIndex]?.push({check: false});
                continue;
            }

            const optionCheck = optionNChecks[featureIndex] as {
                hasFeatureN: BooleanField;
                featureNExplanation: KeyTextField;
            };
            const check = optionCheck.hasFeatureN === true;
            const note = optionCheck.featureNExplanation as string | undefined;

            optionsChecks[optionIndex]?.push({check, note});
        }
    }
</script>
